// src/auditoria/components/tx-data-grid/utils/formatColumns.tsx
//
// ----------------------------------------------------------------------
// RODADA DE CONSERTO DA CAMADA COMPARTILHADA — DECISOES-PENDENTES.md §3.3,
// item (a): "corrigir `formatColumns` para traço + `actionsDisplay` inline
// destrava 73 grades de uma vez — caso de lote de infra".
//
// PEDIDO POR 9 LOTES: L10 (F4), L13 (D2), L16 (D3), L17 (D3), L18, L21, L22,
// L24 (D3, K2), L25. A rodada anterior fechou só o item (b) — o helper
// `colunaAcoes` no `GradeCanvas` —, que exige a tela reescrever a coluna à mão.
// Este arquivo fecha o item (a): quem continua passando `onView`/`onEdit`/
// `onDelete` ao `TxDataGrid` passa a receber a coluna do canvas SEM tocar na tela.
//
// DEFEITO CONFERIDO NESTE ARQUIVO ANTES DE MEXER (versão anterior, linhas 26-78):
//   1. `actionsDisplay = 'menu'` era o DEFAULT, e nenhuma tela do app passa a prop
//      (`grep -rn actionsDisplay src` só encontra a infra: `types.ts:94`,
//      `TxDataGrid.tsx:191/222`). Ou seja: as 18 telas que passam os callbacks
//      recebiam, todas, o menu de três pontos — que o canvas nunca desenha.
//   2. Os ícones eram `solar:eye-bold` / `solar:pen-bold` /
//      `solar:trash-bin-trash-bold` — SÓLIDOS, proibidos por §3.4, que manda usar
//      o conjunto de traço `ds:`.
//   3. `width: 25` contra os 96px do artboard, e sem `align:'right'`.
//   4. A ação de excluir vinha com `sx={{ color:'error.main' }}` PERMANENTE; o
//      canvas põe `error.main` só no hover/foco (`.iconbtn.danger` aplicada sobre
//      o `bare`, como `colunaAcoes` faz).
//
// O QUE MUDOU
//   - O default de `actionsDisplay` passou de `'menu'` para `'inline'`. É a lição
//     da UI-1 citada no briefing: o que depende de alguém ligar uma prop não
//     acontece. Nenhuma chamada existente quebra — a prop continua na assinatura,
//     com o mesmo tipo, e o modo `'menu'` continua alcançável por opção explícita.
//   - O caminho `'inline'` DELEGA a `colunaAcoes` do kit, em vez de reimplementar
//     a coluna. Ver a nota "POR QUE DELEGAR" abaixo.
//   - O modo `'menu'`, que sobrou como escotilha, perdeu os `solar:*-bold`: os
//     itens do menu agora usam `DsIcon` de traço. §3.4 não abre exceção por modo.
//
// POR QUE DELEGAR A `colunaAcoes` EM VEZ DE MONTAR A COLUNA COM `DsIconButton`
// A exigência é que a saída daqui fique VISUALMENTE IDÊNTICA à do helper, para o
// app não ficar com duas aparências de coluna de ação. `DsIconButton` não entrega
// isso sem remendo de `sx`: `tone="bare"` pinta `color:'action.active'`
// (`DsIconButton.tsx`, `SX_BARE`) e o canvas/`colunaAcoes` querem `text.secondary`;
// e `tone="danger"` pinta `error.main` SEMPRE, enquanto `colunaAcoes` só o aplica
// em `&:hover, &:focus-visible`. Reproduzir isso com `sx` seria duplicar o
// `SX_ACAO` do `GradeCanvas` e abrir espaço para as duas cópias divergirem — que é
// exatamente o defeito que §3.3 existe para fechar. Chamando o helper, "idêntico"
// é garantido por construção, hoje e depois de qualquer ajuste no artboard.
//
// O `field` da coluna automática continua sendo `'actions'` (e não o `'acoes'`
// padrão do helper) para não invalidar nenhum `columnVisibilityModel` /
// `initialState` que já cite esse id.
// ----------------------------------------------------------------------

import type { DsAcaoLinha } from 'src/auditoria/components/ds';
import type {
  GridColDef,
  GridRowParams,
  GridActionsColDef,
  GridValidRowModel,
} from '@mui/x-data-grid';

import React from 'react';

import { GridActionsCellItem } from '@mui/x-data-grid';

import { DsIcon, colunaAcoes } from 'src/auditoria/components/ds';

// ----------------------------------------------------------------------

/** Id da coluna automática. Preservado da versão anterior. */
const CAMPO_ACOES = 'actions';

export interface FormatColumnsArgs<T extends GridValidRowModel> {
  columns: GridColDef<T>[];
  onView?: (row: T) => void;
  onEdit?: (row: T) => void;
  onDelete?: (row: T) => void;
  /**
   * `'inline'` (PADRÃO) — a coluna do canvas: 96px, ícones de traço de 18px
   * alinhados à direita em `text.secondary`, via `colunaAcoes`.
   *
   * `'menu'` — o menu de três pontos herdado. Mantido alcançável para quem
   * dependa dele, mas fora do canvas: nenhum artboard desenha esse menu.
   */
  actionsDisplay?: 'inline' | 'menu';
}

export function formatColumns<T extends GridValidRowModel>({
  columns,
  onView,
  onEdit,
  onDelete,
  actionsDisplay = 'inline',
}: FormatColumnsArgs<T>): Array<GridColDef<T> | GridActionsColDef<T>> {
  // ----------------------------------------------------------------------
  // (1) Caminho do canvas — o default.
  // ----------------------------------------------------------------------
  const acoesCanvas: DsAcaoLinha<T>[] = [
    ...(onView
      ? [
          {
            icon: 'olho',
            label: 'Detalhes',
            // `colunaAcoes` já dá `stopPropagation` antes de chamar daqui; o
            // callback da tela continua recebendo só a linha, como sempre.
            onClick: (row: T) => onView(row),
          } satisfies DsAcaoLinha<T>,
        ]
      : []),
    ...(onEdit
      ? [
          {
            icon: 'editar',
            label: 'Editar',
            onClick: (row: T) => onEdit(row),
          } satisfies DsAcaoLinha<T>,
        ]
      : []),
    ...(onDelete
      ? [
          {
            icon: 'excluir',
            label: 'Excluir',
            onClick: (row: T) => onDelete(row),
            tone: 'danger',
          } satisfies DsAcaoLinha<T>,
        ]
      : []),
  ];

  // ----------------------------------------------------------------------
  // (2) Caminho do menu — escotilha explícita, sem ícone sólido.
  // ----------------------------------------------------------------------
  const colunaMenu: GridActionsColDef<T> = {
    type: 'actions',
    field: CAMPO_ACOES,
    headerName: '',
    width: 25,
    getActions: (params: GridRowParams<T>) => [
      ...(onView
        ? [
            <GridActionsCellItem
              key="view"
              icon={<DsIcon name="olho" size={18} />}
              label="Detalhes"
              showInMenu
              onClick={(event: React.MouseEvent<HTMLElement>) => {
                event.stopPropagation();
                onView(params.row);
              }}
            />,
          ]
        : []),
      ...(onEdit
        ? [
            <GridActionsCellItem
              key="edit"
              icon={<DsIcon name="editar" size={18} />}
              label="Editar"
              showInMenu
              onClick={(event: React.MouseEvent<HTMLElement>) => {
                event.stopPropagation();
                onEdit(params.row);
              }}
            />,
          ]
        : []),
      ...(onDelete
        ? [
            <GridActionsCellItem
              key="delete"
              icon={<DsIcon name="excluir" size={18} />}
              label="Excluir"
              showInMenu
              onClick={(event: React.MouseEvent<HTMLElement>) => {
                event.stopPropagation();
                onDelete(params.row);
              }}
              sx={{ color: 'error.main' }}
            />,
          ]
        : []),
    ],
  };

  // 1) map das colunas existentes
  const cols = columns.map((column) => {
    if (column.type === 'date' || column.type === 'dateTime') {
      return {
        ...column,
        valueGetter: (value: string | number | Date) => value && new Date(value),
      } as GridColDef<T>;
    }
    return column;
  });

  // 2) só adiciona a ação automática se não houver nenhuma manual
  //
  // O teste antigo olhava só `type === 'actions'`. A coluna do canvas (a que os
  // ~20 arquivos de §3.3 escreveram à mão, e a que `colunaAcoes` devolve) é um
  // `GridColDef` com `renderCell` e SEM `type`, então passava despercebida: a
  // grade que já tinha a coluna do canvas E passava os callbacks ganharia uma
  // segunda coluna. O id também entra no teste, senão duas colunas disputariam
  // o mesmo `field`.
  const hasManual = cols.some(
    (col) => (col as GridActionsColDef<T>).type === 'actions' || col.field === CAMPO_ACOES
  );
  const hasAtLeastOneAction = !!onView || !!onEdit || !!onDelete;

  if (!hasManual && hasAtLeastOneAction) {
    cols.push(
      actionsDisplay === 'menu' ? colunaMenu : colunaAcoes<T>(acoesCanvas, { field: CAMPO_ACOES })
    );
  }

  return cols;
}
